<template>
  <div class="wfh-modal-mask">
    <div class="wfh-modal-wrapper">
      <div class="wfh-modal-content" v-if="dataIsReady">
        <div class="row">
          <button type="button" @click="closeModal" class="modal-close-btn">
            X
          </button>
        </div>

        <div class="row mb-4">
          <div class="col wfh-modal-header-content">
            <div class="m-header">
              Advanced Task Filter
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col">
            <label class="wfh-label">Team Members</label>
            <div class="wfh-v-select-override wfh-sm">
              <v-select
                  :options="teamMembersOptions"
                  :clearable="true"
                  v-model="selectedTeamMember"
                  :reduce="(item) => item.value"
                  @search="searching"
              >
              </v-select>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col">
            <label class="wfh-label">Label</label>
            <div class="wfh-v-select-override">
              <v-select
                  :options="labelOptions"
                  :filterable="true"
                  :clearable="true"
                  v-model="selectedLabel"
                  :reduce="(item) => item.value"
                  placeholder="Select a label"
              >
              </v-select>
            </div>
          </div>
        </div>

        <div class="pl-3 wfh-modal-fixed-height wfh-modal-content-medium">
          <div class="row mb-3" v-for="customAttribute of customAttributes">
            <div class="col">
              <label class="wfh-label">{{ customAttribute.label }}</label>
              <div class="wfh-v-select-override">
                <v-select
                    :options="customAttribute.options"
                    :filterable="true"
                    :clearable="true"
                    v-model="customAttribute.selected"
                    :reduce="handleSelection"
                    placeholder="Select a value"
                >
                </v-select>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-5 mx-md-auto">
            <button type="button" class="btn btn-block wfh-btn-color-primary wfh-btn-large" @click="submitFilter">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import buildAdvanceFilters from '../../../utils/buildAdvanceFilters';
import {
  getSelectedLabel,
  setCustomAttributes,
  setSelectedLabel,
  setSelectedTeamMember,
} from '../../../repositories/advancedFiltersPersistence';

const LABEL_RELATIONSHIP = 'labels';
const CUSTOM_ATTRIBUTE_RELATIONSHIP = 'customAttributes';
const TEAM_MEMBER_RELATIONSHIP = 'selectedTeamMember';

export default {
  components: {
    vSelect,
    buildAdvanceFilters,
    getSelectedLabel,
    setCustomAttributes,
    setSelectedLabel,
    setSelectedTeamMember,
  },

  props: {
    existingFilters: {
      required: false,
      default: [],
    },
    selectedTeam: {
      required: false,
    },
    searchQueryCached: {
      required: false,
    },
    cachedFields: {
      required: false,
      type: Array,
    },
  },

  data () {
    return {
      selectedLabel: getSelectedLabel(),
      customAttributes: this.cachedFields ?? null,
      selectedTeamMember: null,
      teamMembersOptions: [],
      searchQuery: this.searchQueryCached,
      dataIsReady: false,
    };
  },

  computed: {
    labelOptions () {
      const options = this.$store.state.labelOptions.map((label) => {
        return {
          value: label.id,
          label: label.name,
        };
      });

      if (!options) {
        return [];
      }

      return options;
    },
  },

  watch: {
    selectedLabel () {
      if (this.selectedLabel !== null) {
        this.fetchLabelCustomAttributes();

        return;
      }

      this.clearCustomAttributeFilters();
    },
  },

  methods: {
    searching: _.debounce(async function (searchQuery) {
      this.searchQuery = searchQuery;
      await this.fetchTeamMembers(searchQuery);
    }, 500),

    async fetchLabelCustomAttributes () {
      const res = await axios.get(`/labels/${this.selectedLabel}/custom-attributes?for_filter=1`);

      this.customAttributes = res.data.data.map((attribute) => {
        let value = null;

        for (const existingFilter of this.existingFilters) {
          if (existingFilter.type === CUSTOM_ATTRIBUTE_RELATIONSHIP && existingFilter.id === attribute.id) {
            value = existingFilter.value;
          }
        }

        return {
          id: attribute.id,
          label: attribute.label,
          selected: value,
          options: _.uniq(attribute.options.filter((option) => {
            return option !== null;
          })),
        };
      });
    },

    clearCustomAttributeFilters () {
      this.customAttributes = [];
    },

    handleSelection (item) {
      return item.value ?? item;
    },

    closeModal () {
      this.$emit('close');
    },

    submitFilter () {
      const filters = buildAdvanceFilters(
          this.selectedTeam,
          this.selectedLabel,
          this.selectedTeamMember,
          this.customAttributes,
      );
      setCustomAttributes(this.customAttributes);
      setSelectedLabel(this.selectedLabel);
      setSelectedTeamMember(this.selectedTeamMember);

      this.$emit('submit', filters);
    },

    async fetchTeamMembers (searchQuery = null, loading, initialQuery = false) {
      if (initialQuery === false && !searchQuery) {
        return;
      }

      this.$emit('updateSearchQuery', searchQuery);

      const params = { per_page: 5 };

      if (searchQuery) {
        params.q = searchQuery;
      }

      if (!!this.selectedTeam) {
        const res = await axios.get(`/groups/${this.selectedTeam}/members`, { params });

        this.teamMembersOptions = res.data.data.map((teamMember) => {
          return {
            value: teamMember.id,
            label: teamMember.display_name ?? teamMember.name,
          };
        });

        this.dataIsReady = true;
        return;
      }

      const res = await axios.get(`/team-members`, { params });

      this.teamMembersOptions = res.data.data.map((teamMember) => {
        return {
          value: teamMember.id,
          label: teamMember.display_name ?? teamMember.name,
        };
      });
      this.dataIsReady = true;
    },
  },

  mounted () {
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;

    for (const existingFilter of this.existingFilters) {
      if (existingFilter.type === LABEL_RELATIONSHIP) {
        this.selectedLabel = existingFilter.value;
      }

      if (existingFilter.type === TEAM_MEMBER_RELATIONSHIP) {
        this.selectedTeamMember = existingFilter.value;
      }
    }

    this.fetchTeamMembers(this.searchQuery, false, true);
    this.fetchLabelCustomAttributes();
  },

  beforeDestroy () {
    const scrollY = document.body.style.top;

    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  },
};
</script>
